import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrandedImages, useCompanyBranding, useBrandedColors } from '../hooks/useBranding';
import { vendorRegistrationService } from '../services/vendorRegistration.service';
import { CheckCircle, Circle, ArrowLeft } from 'lucide-react';

// Import form components
import VendorDetailsForm from '../components/VendorRegistration/VendorDetailsForm';
import VendorLocationsForm from '../components/VendorRegistration/VendorLocationsForm';
import VendorServiceRatesForm from '../components/VendorRegistration/VendorServiceRatesForm';
import VendorDriversForm from '../components/VendorRegistration/VendorDriversForm';
import SubmitConfirmation from '../components/VendorRegistration/SubmitConfirmation';

const VendorRegistration = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [registrationData, setRegistrationData] = useState({
        vendor: {},
        locations: [],
        serviceRates: [],
        drivers: []
    });
    const [registrationId, setRegistrationId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [registrationStatus, setRegistrationStatus] = useState(null);
    const [isPending, setIsPending] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    
    const navigate = useNavigate();
    
    // Get branded content
    const { loginLogo } = useBrandedImages();
    const { name, tagline } = useCompanyBranding();
    const colors = useBrandedColors();

    const steps = [
        { title: 'Vendor Details', component: VendorDetailsForm },
        { title: 'Vendor Locations', component: VendorLocationsForm },
        { title: 'Service Rates', component: VendorServiceRatesForm },
        { title: 'Vendor Drivers', component: VendorDriversForm },
        { title: 'Submit', component: SubmitConfirmation }
    ];

    useEffect(() => {
        // Get registration ID from localStorage
        const storedRegistrationId = localStorage.getItem('current_registration_id');
        const storedMobileNumber = localStorage.getItem('vendor_mobile_number');
        
        if (storedRegistrationId) {
            setRegistrationId(parseInt(storedRegistrationId));
            loadExistingData(parseInt(storedRegistrationId), storedMobileNumber);
        } else {
            // If no registration ID, redirect back to register page
            navigate('/register');
        }
    }, [navigate]);

    const loadExistingData = async (regId, mobileNumber) => {
        try {
            setLoading(true);
            const registration = await vendorRegistrationService.getRegistrationById(regId);
            if (registration && registration.registration_data) {
                // Check registration status
                const status = registration.approval_status;
                setRegistrationStatus(status);
                setRejectionReason(status === 'rejected' ? (registration.rejection_reason || '') : '');
                
                // If status is pending, set to pending mode and go to summary page
                if (status === 'pending') {
                    setIsPending(true);
                    setCurrentStep(4); // Go to Submit/Summary step
                }
                // Set the mobile number in the vendor data if not already set
                const vendorData = registration.registration_data.vendor || {};
                if (mobileNumber) {
                    vendorData.contact_number = vendorData.contact_number || mobileNumber;
                    vendorData.owner_mobile = vendorData.owner_mobile || mobileNumber;
                }
                
                // Ensure address is in object format
                if (vendorData.address && typeof vendorData.address === 'string') {
                    vendorData.address = {
                        address: vendorData.address,
                        state: '',
                        city: '',
                        latitude: null,
                        longitude: null,
                        pincode: ''
                    };
                } else if (!vendorData.address) {
                    vendorData.address = {
                        address: '',
                        state: '',
                        city: '',
                        latitude: null,
                        longitude: null,
                        pincode: ''
                    };
                }
                
                // Handle locations data structure
                const locationsData = registration.registration_data.locations || [];
                const processedLocations = locationsData.map(location => {
                    // Convert old string address to new JSON structure if needed
                    if (location.address && typeof location.address === 'string') {
                        return {
                            ...location,
                            location_address: {
                                address: location.address,
                                state: location.state || '',
                                city: location.city || '',
                                latitude: location.latitude || null,
                                longitude: location.longitude || null,
                                pincode: location.pincode || ''
                            },
                            // Ensure operating_hours exists
                            operating_hours: location.operating_hours || {
                                monday: { open: '06:00', close: '18:00' },
                                tuesday: { open: '06:00', close: '18:00' },
                                wednesday: { open: '06:00', close: '18:00' },
                                thursday: { open: '06:00', close: '18:00' },
                                friday: { open: '06:00', close: '18:00' },
                                saturday: { open: '07:00', close: '16:00' },
                                sunday: { open: '08:00', close: '14:00' }
                            },
                            // Ensure vendor_location_type exists
                            vendor_location_type: location.vendor_location_type || 'VENDOR_ONLY',
                            // Map old contact fields to new manager fields
                            manager_name: location.manager_name || location.contact_person || '',
                            manager_mobile: location.manager_mobile || location.contact_number || '',
                            manager_email: location.manager_email || ''
                        };
                    }
                    return location;
                });
                
                // Handle service rates data structure
                const serviceRatesData = registration.registration_data.serviceRates || [];
                const processedServiceRates = serviceRatesData.map(rate => {
                    // Ensure all required fields exist with proper defaults
                    return {
                        service_type_code: rate.service_type_code || '',
                        service_subtype_code: rate.service_subtype_code || '',
                        time_slot: rate.time_slot || 'DAY',
                        time_slot_start: rate.time_slot_start || '06:00:00',
                        time_slot_end: rate.time_slot_end || '18:00:00',
                        base_service_rate: rate.base_service_rate || '',
                        base_max_kms: rate.base_max_kms || '',
                        additional_rate_per_km: rate.additional_rate_per_km || '',
                        fixed_rate: rate.fixed_rate || '',
                        cancel_charges_per_km_upto_base_kms: rate.cancel_charges_per_km_upto_base_kms || '',
                        cancel_charges_per_km_above_base_kms: rate.cancel_charges_per_km_above_base_kms || '',
                        effective_from: rate.effective_from || new Date().toISOString().split('T')[0],
                        effective_to: rate.effective_to || ''
                    };
                });
                
                // Handle drivers data structure
                const driversData = registration.registration_data.drivers || [];
                const processedDrivers = driversData.map(driver => {
                    // Ensure all required fields exist with proper defaults
                    return {
                        // User fields (will be created in users table)
                        username: driver.username || '',
                        name: driver.name || '',
                        mobile_number: driver.mobile_number || '',
                        // Driver-specific fields
                        license_number: driver.license_number || '',
                        license_expiry: driver.license_expiry || '',
                        vehicle_number: driver.vehicle_number || '',
                        experience_years: driver.experience_years || '',
                        // Vendor driver relationship
                        vendor_location_id: driver.vendor_location_id || '',
                        is_active: driver.is_active !== undefined ? driver.is_active : true
                    };
                });
                
                setRegistrationData({
                    ...registration.registration_data,
                    vendor: vendorData,
                    locations: processedLocations,
                    serviceRates: processedServiceRates,
                    drivers: processedDrivers
                });
            }
        } catch (err) {
            console.error('Error loading registration data:', err);
            setError('Failed to load existing registration data');
        } finally {
            setLoading(false);
        }
    };

    const handleNext = async (stepData) => {
        // Prevent updates if registration is pending
        if (isPending) {
            return;
        }

        try {
            setLoading(true);
            setError('');

            // Update registration data with current step data
            const updatedData = {
                ...registrationData,
                ...stepData
            };
            setRegistrationData(updatedData);

            // Auto-save the registration in the database
            await vendorRegistrationService.updateRegistration(registrationId, {
                registration_data: updatedData
            });

            // Move to next step
            setCurrentStep(prev => prev + 1);
        } catch (err) {
            console.error('Error updating registration:', err);
            setError('Failed to save data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePrevious = async () => {
        // Prevent navigation if registration is pending
        if (isPending) {
            return;
        }
        
        try {
            setLoading(true);
            setError('');
            
            // Auto-save current data before moving back
            await vendorRegistrationService.updateRegistration(registrationId, {
                registration_data: registrationData
            });
            
            setCurrentStep(prev => prev - 1);
        } catch (err) {
            console.error('Error saving data:', err);
            setError('Failed to save data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (stepData) => {
        // Prevent saving if registration is pending
        if (isPending) {
            // Clear localStorage and navigate without saving
            localStorage.removeItem('current_registration_id');
            localStorage.removeItem('vendor_mobile_number');
            navigate('/register');
            return;
        }

        try {
            setLoading(true);
            setError('');

            // Update registration data with current step data
            const updatedData = {
                ...registrationData,
                ...stepData
            };
            setRegistrationData(updatedData);

            // Save the registration in the database without submitting
            await vendorRegistrationService.updateRegistration(registrationId, {
                registration_data: updatedData
            });

            // Show success message
            setError('');
            alert('Registration saved successfully! You can continue later.');
            
            // Clear localStorage
            localStorage.removeItem('current_registration_id');
            localStorage.removeItem('vendor_mobile_number');
            
            // Navigate back to register page
            navigate('/register');
        } catch (err) {
            console.error('Error saving registration:', err);
            setError('Failed to save data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            setError('');

            // Submit the registration for approval
            await vendorRegistrationService.submitRegistration(registrationId);

            // Clear all registration data from localStorage
            localStorage.removeItem('current_registration_id');
            localStorage.removeItem('vendor_mobile_number');

            // Redirect to login with success message
            navigate('/login', {
                state: {
                    message: 'Vendor registration submitted successfully! Your application is under review. You will be notified once approved.'
                }
            });
        } catch (err) {
            console.error('Error submitting registration:', err);
            setError('Failed to submit registration. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackToRegister = () => {
        // Clear all registration data
        localStorage.removeItem('current_registration_id');
        localStorage.removeItem('vendor_mobile_number');
        navigate('/register');
    };

    if (loading && currentStep === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: colors.primary }}></div>
                    <p className="text-gray-600">Loading registration data...</p>
                </div>
            </div>
        );
    }

    const CurrentStepComponent = steps[currentStep].component;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center space-x-3 min-w-0">
                            {loginLogo && (
                                <img src={loginLogo} alt="Logo" className="h-8 w-auto shrink-0" />
                            )}
                            <div className="min-w-0">
                                <h1 className="text-lg md:text-xl font-semibold text-gray-900 truncate">{name}</h1>
                                <p className="text-sm text-gray-600 hidden sm:block">{tagline}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleBackToRegister}
                            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors shrink-0 text-sm"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span className="hidden sm:inline">Back to Register</span>
                            <span className="sm:hidden">Back</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-white border-b">
                <div className="max-w-4xl mx-auto px-4 py-4 md:py-6">
                    {/* Mobile: current step indicator */}
                    <div className="md:hidden">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900">
                                Step {currentStep + 1} of {steps.length}
                            </span>
                            <span className="text-sm text-gray-600">{steps[currentStep].title}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="h-2 rounded-full transition-all duration-300"
                                style={{
                                    width: `${((currentStep + 1) / steps.length) * 100}%`,
                                    backgroundColor: colors.primary
                                }}
                            />
                        </div>
                        <div className="flex justify-between mt-3 overflow-x-auto gap-1">
                            {steps.map((step, index) => (
                                <div key={index} className="flex flex-col items-center min-w-0 flex-1">
                                    {(index <= currentStep || isPending) ? (
                                        <CheckCircle className="w-5 h-5" style={{ color: colors.primary }} />
                                    ) : (
                                        <Circle className="w-5 h-5 text-gray-300" />
                                    )}
                                    <span className={`mt-1 text-[10px] text-center leading-tight ${
                                        (index <= currentStep || isPending) ? 'text-gray-900' : 'text-gray-400'
                                    }`}>
                                        {step.title.split(' ')[0]}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Desktop: full horizontal stepper */}
                    <div className="hidden md:flex items-center justify-between">
                        {steps.map((step, index) => (
                            <div key={index} className="flex items-center">
                                <div className="flex items-center">
                                    {(index <= currentStep || isPending) ? (
                                        <CheckCircle 
                                            className="w-6 h-6" 
                                            style={{ color: colors.primary }}
                                        />
                                    ) : (
                                        <Circle className="w-6 h-6 text-gray-300" />
                                    )}
                                    <span className={`ml-2 text-sm font-medium ${
                                        (index <= currentStep || isPending) ? 'text-gray-900' : 'text-gray-500'
                                    }`}>
                                        {step.title}
                                    </span>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`w-16 h-0.5 mx-4 ${
                                        (index < currentStep || isPending) ? 'bg-gray-300' : 'bg-gray-200'
                                    }`} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 md:py-8">
                {/* Pending Status Banner */}
                {isPending && (
                    <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
                        <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3 shrink-0"></div>
                            <span className="font-medium">Registration Pending Approval</span>
                        </div>
                        <p className="text-sm mt-1 text-blue-600">
                            Your vendor registration has been submitted and is currently under review. You cannot make changes while it's pending approval.
                        </p>
                    </div>
                )}

                {registrationStatus === 'rejected' && (
                    <div role="alert" className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        <span className="font-medium">Your registration was not approved</span>
                        {rejectionReason && (
                            <p className="text-sm mt-1 whitespace-pre-line break-words">
                                <span className="font-medium">Reason:</span> {rejectionReason}
                            </p>
                        )}
                        <p className="text-sm mt-1 text-red-600">
                            Please correct the details below and submit your registration again.
                        </p>
                    </div>
                )}

                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                        {error}
                    </div>
                )}

                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-4 md:p-6">
                        <CurrentStepComponent
                            data={registrationData}
                            onNext={handleNext}
                            onPrevious={handlePrevious}
                            onSubmit={handleSubmit}
                            loading={loading}
                            isSubmitting={isSubmitting}
                            currentStep={currentStep}
                            totalSteps={steps.length}
                            colors={colors}
                            onSave={handleSave}
                            isPending={isPending}
                            registrationStatus={registrationStatus}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VendorRegistration; 